import { useState, useCallback } from 'react';
import moment from 'moment';
import { toast } from 'react-toastify';

/**
 * Hook personnalisé pour gérer la densité des tâches dans un calendrier
 * @param {number} threshold - Seuil de tâches par semaine (défaut: 15)
 * @returns {object} - Objet contenant les états et fonctions pour gérer la densité
 */
const useCalendarDensity = (threshold = 15) => {
  const [calendarView, setCalendarView] = useState("timeGridWeek");
  const [currentView, setCurrentView] = useState("timeGridWeek");
  const [forceRerender, setForceRerender] = useState(0);

  // Fonction pour analyser la densité par semaine
  const analyzeWeekDensity = useCallback((tasks, taskThreshold) => {
    const weeksMap = new Map();
    
    tasks.forEach(task => {
      const taskStart = moment(task.start);
      const taskEnd = moment(task.end);
      
      const currentWeek = taskStart.clone().startOf('isoWeek');
      const endWeek = taskEnd.clone().startOf('isoWeek');
      
      while (currentWeek.isSameOrBefore(endWeek, 'week')) {
        const weekKey = currentWeek.format('YYYY-[W]WW');
        
        if (!weeksMap.has(weekKey)) {
          weeksMap.set(weekKey, []);
        }
        weeksMap.get(weekKey).push(task);
        
        currentWeek.add(1, 'week');
      }
    });

    let maxTasksInWeek = 0;
    let mostLoadedWeek = '';
    
    for (const [weekKey, weekTasks] of weeksMap) {
      if (weekTasks.length > maxTasksInWeek) {
        maxTasksInWeek = weekTasks.length;
        mostLoadedWeek = weekKey;
      }
    }
    
    return {
      shouldUseMonthView: maxTasksInWeek > taskThreshold,
      maxTasksInWeek: maxTasksInWeek,
      mostLoadedWeek: mostLoadedWeek
    };
  }, []);

  // Fonction pour analyser la densité de la semaine visible
  const analyzeVisibleWeekDensity = useCallback((tasks, visibleStart, visibleEnd, currentViewName) => {
    if (currentViewName !== "timeGridWeek") {
      return { shouldUseMonthView: false, maxTasksInWeek: 0, mostLoadedWeek: '' };
    }

    const visibleStartMoment = moment(visibleStart);
    const visibleEndMoment = moment(visibleEnd);
    
    const visibleWeekTasks = tasks.filter(task => {
      const taskStart = moment(task.start);
      const taskEnd = moment(task.end);
      
      return taskStart.isBefore(visibleEndMoment) && taskEnd.isAfter(visibleStartMoment);
    });
    
    const weekAnalysis = analyzeWeekDensity(visibleWeekTasks, threshold);
    
    return weekAnalysis;
  }, [analyzeWeekDensity, threshold]);

  // Callback pour gérer les changements de dates/vue
  const handleDatesSet = useCallback((info, tasks) => {
    const newView = info.view.type;
    setCurrentView(newView);
    
    if (newView === "timeGridWeek") {
      const weekAnalysis = analyzeVisibleWeekDensity(tasks, info.start, info.end, newView);
      
      if (weekAnalysis.shouldUseMonthView) {
        setCalendarView("dayGridMonth");
        setForceRerender(prev => prev + 1);
        toast.info(`Semaine trop chargée (${weekAnalysis.maxTasksInWeek} tâches). Passage automatique en vue mois.`);
      }
    }
  }, [analyzeVisibleWeekDensity]);

  // Fonction pour analyser les tâches au chargement initial
  const analyzeInitialTasks = useCallback((tasks) => {
    const weekAnalysis = analyzeWeekDensity(tasks, threshold);
    
    if (weekAnalysis.shouldUseMonthView) {
      setCalendarView("dayGridMonth");
      toast.info(`Semaine trop chargée (${weekAnalysis.maxTasksInWeek} tâches). Passage automatique en vue mois.`);
    } else {
      setCalendarView("timeGridWeek");
    }
  }, [analyzeWeekDensity, threshold]);

  return {
    calendarView,
    currentView,
    forceRerender,
    handleDatesSet,
    analyzeInitialTasks
  };
};

export default useCalendarDensity;
